import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Search, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  clearStudioSubscriptionModelManual,
  fetchStudioSubscriptionHistory,
  fetchStudioSubscriptionModels,
  setStudioSubscriptionModelManual,
  type StudioSubscriptionHistoryRow,
  type StudioSubscriptionModelRow,
} from '@/services/studioSubscriptionModelService'
import type { StudioSubscriptionType } from '@/types'

const SUBSCRIPTION_LABELS: Record<StudioSubscriptionType, string> = {
  REGULAR: 'абонементы',
  SUBSCRIPTION: 'подписки',
  MIXED: 'гибрид',
}

const STATUS_LABELS: Record<string, string> = {
  CLOSED: 'закрыта',
  OPEN: 'открыта',
  SUSPENDED: 'приостановлена',
  CANCELLED: 'отменена',
  'PRE-OPEN': 'открывается',
  SWITCH_TO_PURCHASE: 'переход на покупку',
  SERVICE: 'нет продаж',
}

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

function subscriptionLabel(value: StudioSubscriptionType | string | null | undefined): string {
  if (!value) return '—'
  return SUBSCRIPTION_LABELS[value as StudioSubscriptionType] ?? value
}

function studioLabel(row: StudioSubscriptionModelRow): string {
  return `№${row.studio_number ?? '—'} · ${row.city_name ?? 'город не указан'}`
}

function isMismatch(row: StudioSubscriptionModelRow): boolean {
  return row.contract_sales_model !== null && row.contract_sales_model !== row.subscription_type
}

function matchesQuery(row: StudioSubscriptionModelRow, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return [row.studio_number, row.city_name, row.subscription_type_manual_reason]
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

/**
 * Модель продаж студии вне договора. Обычно модель задаёт условие договора (карточка условий:
 * «Обычные» / «Модель 1 — подписки» / «Модель 2 — гибрид»), и сервер ведёт её сам. Ручная
 * отметка — только для исключений, где модель не следует из договора; синк её не трогает.
 */
export function StudioSubscriptionModelModal({
  onBack,
  onClose,
  onDone,
}: {
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const [rows, setRows] = useState<StudioSubscriptionModelRow[]>([])
  const [historyRows, setHistoryRows] = useState<StudioSubscriptionHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [onlyExceptions, setOnlyExceptions] = useState(true)
  const [studioInternalId, setStudioInternalId] = useState<number | null>(null)
  const [manualType, setManualType] = useState<StudioSubscriptionType>('SUBSCRIPTION')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function loadRows() {
    setLoading(true)
    try {
      setRows(await fetchStudioSubscriptionModels())
      setError(null)
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить модели студий'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadRows()
  }, [])

  const exceptionCount = useMemo(
    () => rows.filter(row => row.subscription_type_source === 'MANUAL' || isMismatch(row)).length,
    [rows]
  )

  const filteredRows = useMemo(() => {
    return rows
      .filter(row => !onlyExceptions || row.subscription_type_source === 'MANUAL' || isMismatch(row))
      .filter(row => matchesQuery(row, query))
      .slice(0, 80)
  }, [rows, onlyExceptions, query])

  const selected = useMemo(
    () => rows.find(row => row.studio_internal_id === studioInternalId) ?? null,
    [rows, studioInternalId]
  )

  useEffect(() => {
    if (!selected) return
    setManualType(selected.subscription_type ?? 'SUBSCRIPTION')
    setReason(selected.subscription_type_manual_reason ?? '')
  }, [selected])

  useEffect(() => {
    let cancelled = false

    async function loadHistory() {
      if (!selected?.studio_number) {
        setHistoryRows([])
        return
      }
      setHistoryLoading(true)
      try {
        const history = await fetchStudioSubscriptionHistory(selected.studio_number)
        if (!cancelled) setHistoryRows(history)
      } catch (historyError: unknown) {
        if (!cancelled) setError(errorMessage(historyError, 'Не удалось загрузить историю модели'))
      } finally {
        if (!cancelled) setHistoryLoading(false)
      }
    }

    void loadHistory()
    return () => {
      cancelled = true
    }
  }, [selected])

  async function run(action: () => Promise<void>, message: string) {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await action()
      setSuccess(message)
      await loadRows()
      if (selected?.studio_number) setHistoryRows(await fetchStudioSubscriptionHistory(selected.studio_number))
      onDone?.()
    } catch (actionError: unknown) {
      setError(errorMessage(actionError, 'Операция не выполнена'))
    } finally {
      setSaving(false)
    }
  }

  const canSetManual = !saving && selected !== null && reason.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8" onClick={onClose}>
      <section
        className="my-6 w-full max-w-3xl rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Вернуться к выбору кейса"
                aria-label="Вернуться к выбору кейса"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">Модель студии вне договора</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Модель продаж задаётся условиями договора («Обычные», «Модель 1 — подписки», «Модель 2 — гибрид») и
                меняется новым интервалом условий с датой. Здесь — только исключения, где модель не следует из
                договора: подписки по абонементному договору, студия УК без договора.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {success && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{success}</div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0">
            <label className="block text-xs font-medium text-muted-foreground">
              Студия
              <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
                <Search size={15} className="text-muted-foreground" />
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  placeholder="Номер, город, причина"
                />
              </div>
            </label>
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={onlyExceptions} onChange={event => setOnlyExceptions(event.target.checked)} />
              Только ручные и расхождения с договором ({exceptionCount})
            </label>

            {loading ? (
              <div className="mt-1 flex h-[154px] items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                Загрузка
              </div>
            ) : (
              <select
                value={studioInternalId ?? ''}
                onChange={event => {
                  const value = event.target.value
                  setStudioInternalId(value ? Number(value) : null)
                  setSuccess(null)
                  setError(null)
                }}
                size={8}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                {filteredRows.map(row => (
                  <option key={row.studio_internal_id} value={row.studio_internal_id}>
                    {studioLabel(row)} · {subscriptionLabel(row.subscription_type)}
                    {row.subscription_type_source === 'MANUAL' ? ' (вручную)' : ''}
                    {isMismatch(row) && row.subscription_type_source !== 'MANUAL' ? ' · расходится с договором' : ''}
                  </option>
                ))}
              </select>
            )}
            <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
              Показано {filteredRows.length} из {onlyExceptions ? exceptionCount : rows.length}
            </span>
          </div>

          <div className="min-w-0 space-y-3">
            {selected && (
              <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <div className="text-sm font-semibold text-foreground">{studioLabel(selected)}</div>
                <div className="mt-1">
                  Сейчас: <span className="font-medium text-foreground">{subscriptionLabel(selected.subscription_type)}</span>
                  {' · '}
                  {selected.subscription_type_source === 'MANUAL' ? 'ручная отметка' : 'по договору'}
                  {' · '}
                  {STATUS_LABELS[selected.status_code ?? ''] ?? selected.status_code ?? '—'}
                </div>
                <div className="mt-1">
                  По условиям договора на сегодня:{' '}
                  {selected.contract_sales_model
                    ? `${subscriptionLabel(selected.contract_sales_model)} (условие #${selected.contract_term_id} с ${formatDate(selected.contract_term_from)})`
                    : 'условий нет — модель не меняется автоматически'}
                </div>
                {selected.subscription_type_source === 'MANUAL' && (
                  <div className="mt-1">Причина: {selected.subscription_type_manual_reason}</div>
                )}
                <div className="mt-1">
                  Дата первой продажи подписки: {formatDate(selected.first_subscription_sale_date)}
                  {selected.first_sale_date_source === 'MANUAL' ? ' (утверждена вручную)' : ''}
                </div>
              </div>
            )}

            {selected && (
              <div className="space-y-2 rounded-md border border-border bg-background p-3">
                <label className="block text-xs font-medium text-muted-foreground">
                  Ручная модель
                  <select
                    value={manualType}
                    onChange={event => setManualType(event.target.value as StudioSubscriptionType)}
                    className={INPUT_CLASS}
                  >
                    <option value="REGULAR">Абонементы</option>
                    <option value="SUBSCRIPTION">Подписки</option>
                    <option value="MIXED">Гибрид</option>
                  </select>
                </label>
                <label className="block text-xs font-medium text-muted-foreground">
                  Причина (обязательно)
                  <textarea
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    rows={2}
                    className={INPUT_CLASS}
                    placeholder="Почему модель не следует из договора"
                  />
                </label>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {selected.subscription_type_source === 'MANUAL' && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={saving}
                      onClick={() => run(
                        () => clearStudioSubscriptionModelManual(selected.studio_internal_id),
                        `Студия №${selected.studio_number}: модель снова по договору.`
                      )}
                    >
                      Вернуть модель по договору
                    </Button>
                  )}
                  <Button
                    type="button"
                    disabled={!canSetManual}
                    onClick={() => run(
                      () => setStudioSubscriptionModelManual({
                        studioInternalId: selected.studio_internal_id,
                        subscriptionType: manualType,
                        reason: reason.trim(),
                      }),
                      `Студия №${selected.studio_number}: ручная модель «${subscriptionLabel(manualType)}».`
                    )}
                  >
                    {saving && <Loader2 size={15} className="mr-2 animate-spin" />}
                    {selected.subscription_type_source === 'MANUAL' ? 'Сохранить ручную модель' : 'Поставить ручную модель'}
                  </Button>
                </div>
              </div>
            )}

            {selected && (
              <div className="rounded-md border border-border bg-background p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold text-foreground">История модели</div>
                  {historyLoading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
                </div>
                {historyRows.filter(row => row.changed_at).length === 0 ? (
                  <div className="text-xs text-muted-foreground">Переходов пока нет</div>
                ) : (
                  <div className="max-h-36 space-y-1 overflow-y-auto text-xs">
                    {historyRows.filter(row => row.changed_at).map(row => (
                      <div key={`${row.changed_at}-${row.old_subscription_type}-${row.new_subscription_type}`} className="rounded border border-border bg-card px-2 py-1">
                        <span className="font-medium text-foreground">
                          {subscriptionLabel(row.old_subscription_type)} {'→'} {subscriptionLabel(row.new_subscription_type)}
                        </span>
                        <span className="ml-2 text-muted-foreground">{formatDate(row.changed_at)}</span>
                        {row.changed_by?.startsWith('contract_terms#') && (
                          <span className="ml-2 text-muted-foreground">по условию #{row.changed_by.slice('contract_terms#'.length)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
